var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState, useContext, useRef, forwardRef } from "react";
import { apolloSpaceInlineHalfX, apolloSpaceInsetHalfX } from "@nexthink/apollo-tokens";
import Box from "../box/Box";
import { StyledWrapperLabel, StyledInput, StyledWrapper, StyledErrorIcon } from "./Radio.style";
import { RadioGroupContext } from "./RadioGroup";
import { StyledHiddenScreenReaderOnly } from "../../utils/StyleUtils";
import useAutoFocus from "../../hooks/useAutoFocus";
import useUniqueId from "../../hooks/useUniqueId";
import useIdAttrs from "../../hooks/useIdAttrs";
import InfoIconTooltip from "../with-tooltip/InfoIconTooltip";
import Flex from "../box/Flex";
const prefixId = "radio-";
const useInputAriaProps = ({ checked, error, label }, errorId, labelId) => (Object.assign(Object.assign({}, (label && { "aria-labelledby": labelId })), (error &&
    checked && Object.assign({ "aria-invalid": true }, (typeof error === "string" && { "aria-errormessage": errorId })))));
const useRadio = (_a) => {
    var { value, label, name, disabled, infoTooltip, id: defaultId, "data-insights": insightsAttr, error, autoFocus: initialAutoFocus, display, checked, onChange, 
    // tooltip target props
    "aria-describedby": ariaDescribedBy, "aria-hidden": ariaHidden, "data-portal-target": dataPortalTarget, onKeyDown, onMouseEnter, onMouseLeave, onPointerEnter, onPointerLeave } = _a, restAriaProps = __rest(_a, ["value", "label", "name", "disabled", "infoTooltip", "id", "data-insights", "error", "autoFocus", "display", "checked", "onChange", "aria-describedby", "aria-hidden", "data-portal-target", "onKeyDown", "onMouseEnter", "onMouseLeave", "onPointerEnter", "onPointerLeave"]);
    const id = useUniqueId(prefixId, defaultId);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const ref = useRef(null);
    const groupContext = useContext(RadioGroupContext);
    const selected = groupContext ? value === groupContext.selectedOption : checked;
    const autoFocus = initialAutoFocus && !disabled;
    const [focused, setFocused] = useState(autoFocus || ((groupContext === null || groupContext === void 0 ? void 0 : groupContext.groupAutoFocus) && selected));
    const onChangeSelection = groupContext ? groupContext.onChangeOption : onChange;
    useAutoFocus(ref, autoFocus || ((groupContext === null || groupContext === void 0 ? void 0 : groupContext.groupAutoFocus) && selected));
    const errorIdProps = idAttrs("-error");
    const labelIdProps = idAttrs("-label");
    const ariaInputProps = useInputAriaProps({ checked: selected, error, label }, errorIdProps.id, labelIdProps.id);
    return {
        idAttrs,
        selected,
        wrapperProps: {
            display: display || (groupContext === null || groupContext === void 0 ? void 0 : groupContext.groupDisplay),
            // tooltip target props
            "aria-describedby": ariaDescribedBy,
            "aria-hidden": ariaHidden,
            onKeyDown,
            onMouseEnter,
            onMouseLeave,
            onPointerEnter,
            onPointerLeave,
            "data-portal-target": dataPortalTarget,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
        },
        wrapperLabelProps: {
            checked: selected,
            disabled: disabled || (groupContext === null || groupContext === void 0 ? void 0 : groupContext.groupDisabled),
            error,
            focused,
            label,
        },
        errorProps: Object.assign({}, errorIdProps),
        labelProps: Object.assign(Object.assign({}, labelIdProps), { children: label }),
        inputProps: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ ref, type: "radio" }, idAttrs()), { name: name || (groupContext === null || groupContext === void 0 ? void 0 : groupContext.name), value, disabled: disabled || (groupContext === null || groupContext === void 0 ? void 0 : groupContext.groupDisabled), checked: selected }), (!disabled && {
            onChange: () => onChangeSelection === null || onChangeSelection === void 0 ? void 0 : onChangeSelection({ value, label, disabled }),
        })), ariaInputProps), restAriaProps),
    };
};
const Radio = forwardRef(function Radio(props, forwardedRef) {
    const { label, disabled, id, "data-insights": insightsAttr, error, infoTooltip } = props;
    const { selected, wrapperProps, wrapperLabelProps, errorProps, labelProps, inputProps } = useRadio(props);
    const renderError = useCallback((errorMessage) => (React.createElement(Box, Object.assign({}, errorProps, { as: "span", display: "inline-flex", pl: apolloSpaceInlineHalfX }, (errorMessage && { "aria-live": "polite" })),
        React.createElement(Box, { as: "span", display: "inline-flex", width: "100%", height: "100%", title: errorMessage, "aria-hidden": "true" },
            React.createElement(StyledErrorIcon, null)),
        errorMessage && React.createElement(StyledHiddenScreenReaderOnly, null, errorMessage))), [id, insightsAttr]);
    const hasError = error && selected && !disabled;
    const hasErrorMessage = typeof error === "string";
    return (React.createElement(StyledWrapper, Object.assign({}, wrapperProps, { ref: forwardedRef }),
        React.createElement(StyledWrapperLabel, Object.assign({}, wrapperLabelProps),
            React.createElement(StyledInput, Object.assign({}, inputProps)),
            label && React.createElement("span", Object.assign({}, labelProps)),
            hasError && renderError(hasErrorMessage ? error : undefined)),
        infoTooltip && (React.createElement(Flex, { ml: apolloSpaceInsetHalfX, alignItems: "center", onFocus: (e) => {
                e.stopPropagation();
            } },
            React.createElement(InfoIconTooltip, null, infoTooltip)))));
});
export default Radio;
//# sourceMappingURL=Radio.js.map